export enum HealthScore {
  A_PLUS = "A+",
  A = "A",
  B = "B",
  C = "C",
  D = "D",
}

export interface HealthIndexConditions {
  has_profile_description?: boolean;
  has_regular_hours_periods?: boolean;
  has_min_photo_count?: boolean;
  has_food_menus?: boolean;
  has_service_items?: boolean;
  days_since_last_post?: number;
  manages_gbp_review_replies?: boolean;
  review_reply_rate?: number;
}

export interface HealthIndexResponse {
  currentHealthIndex: HealthScore;
  conditions: HealthIndexConditions;
}

export interface HealthIndexConditionItem {
  key: string;
  translationKey: string;
  checkFunction: (conditions: HealthIndexConditions) => boolean;
  requiredForScores: HealthScore[];
}

// 条件チェック関数 / Condition check functions
export const checkHasProfileDescription = (
  conditions: HealthIndexConditions
): boolean => conditions.has_profile_description === true;

export const checkHasRegularHoursPeriods = (
  conditions: HealthIndexConditions
): boolean => conditions.has_regular_hours_periods === true;

export const checkHasMinPhotoCount = (
  conditions: HealthIndexConditions
): boolean => conditions.has_min_photo_count === true;

export const checkHasFoodMenus = (conditions: HealthIndexConditions): boolean =>
  conditions.has_food_menus !== undefined ? conditions.has_food_menus : true;

export const checkHasServiceItems = (
  conditions: HealthIndexConditions
): boolean =>
  conditions.has_service_items !== undefined
    ? conditions.has_service_items
    : true;

export const checkDaysSinceLastPost14 = (
  conditions: HealthIndexConditions
): boolean =>
  conditions.days_since_last_post !== undefined &&
  conditions.days_since_last_post >= 0 &&
  conditions.days_since_last_post <= 14;

export const checkDaysSinceLastPost60 = (
  conditions: HealthIndexConditions
): boolean =>
  conditions.days_since_last_post !== undefined &&
  conditions.days_since_last_post >= 0 &&
  conditions.days_since_last_post <= 60;

export const checkManagesGbpReviewReplies = (
  conditions: HealthIndexConditions
): boolean => conditions.manages_gbp_review_replies === true;

export const checkReviewReplyRate80 = (
  conditions: HealthIndexConditions
): boolean =>
  conditions.review_reply_rate !== undefined &&
  conditions.review_reply_rate >= 80;

// 各スコアの条件定義 / Score conditions definition
export const HEALTH_INDEX_CONDITIONS: Record<
  HealthScore,
  HealthIndexConditionItem[]
> = {
  [HealthScore.A_PLUS]: [
    {
      key: "has_profile_description",
      translationKey: "has_profile_description",
      checkFunction: checkHasProfileDescription,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
    {
      key: "days_since_last_post_14",
      translationKey: "days_since_last_post_14",
      checkFunction: checkDaysSinceLastPost14,
      requiredForScores: [HealthScore.A_PLUS, HealthScore.A],
    },
    {
      key: "manages_gbp_review_replies",
      translationKey: "manages_gbp_review_replies",
      checkFunction: checkManagesGbpReviewReplies,
      requiredForScores: [HealthScore.A_PLUS],
    },
    {
      key: "has_regular_hours_periods",
      translationKey: "has_regular_hours_periods",
      checkFunction: checkHasRegularHoursPeriods,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
    {
      key: "has_food_menus",
      translationKey: "has_food_menus",
      checkFunction: checkHasFoodMenus,
      requiredForScores: [HealthScore.A_PLUS, HealthScore.A, HealthScore.B],
    },
    {
      key: "review_reply_rate_80",
      translationKey: "review_reply_rate_80",
      checkFunction: checkReviewReplyRate80,
      requiredForScores: [HealthScore.A_PLUS],
    },
    {
      key: "has_min_photo_count",
      translationKey: "has_min_photo_count",
      checkFunction: checkHasMinPhotoCount,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
    {
      key: "has_service_items",
      translationKey: "has_service_items",
      checkFunction: checkHasServiceItems,
      requiredForScores: [HealthScore.A_PLUS, HealthScore.A, HealthScore.B],
    },
  ],
  [HealthScore.A]: [
    {
      key: "has_profile_description",
      translationKey: "has_profile_description",
      checkFunction: checkHasProfileDescription,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
    {
      key: "days_since_last_post_14",
      translationKey: "days_since_last_post_14",
      checkFunction: checkDaysSinceLastPost14,
      requiredForScores: [HealthScore.A_PLUS, HealthScore.A],
    },
    {
      key: "has_regular_hours_periods",
      translationKey: "has_regular_hours_periods",
      checkFunction: checkHasRegularHoursPeriods,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
    {
      key: "has_food_menus",
      translationKey: "has_food_menus",
      checkFunction: checkHasFoodMenus,
      requiredForScores: [HealthScore.A_PLUS, HealthScore.A, HealthScore.B],
    },
    {
      key: "has_min_photo_count",
      translationKey: "has_min_photo_count",
      checkFunction: checkHasMinPhotoCount,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
    {
      key: "has_service_items",
      translationKey: "has_service_items",
      checkFunction: checkHasServiceItems,
      requiredForScores: [HealthScore.A_PLUS, HealthScore.A, HealthScore.B],
    },
  ],
  [HealthScore.B]: [
    {
      key: "has_profile_description",
      translationKey: "has_profile_description",
      checkFunction: checkHasProfileDescription,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
    {
      key: "days_since_last_post_60",
      translationKey: "days_since_last_post_60",
      checkFunction: checkDaysSinceLastPost60,
      requiredForScores: [HealthScore.B],
    },
    {
      key: "has_regular_hours_periods",
      translationKey: "has_regular_hours_periods",
      checkFunction: checkHasRegularHoursPeriods,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
    {
      key: "has_food_menus",
      translationKey: "has_food_menus",
      checkFunction: checkHasFoodMenus,
      requiredForScores: [HealthScore.A_PLUS, HealthScore.A, HealthScore.B],
    },
    {
      key: "has_min_photo_count",
      translationKey: "has_min_photo_count",
      checkFunction: checkHasMinPhotoCount,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
    {
      key: "has_service_items",
      translationKey: "has_service_items",
      checkFunction: checkHasServiceItems,
      requiredForScores: [HealthScore.A_PLUS, HealthScore.A, HealthScore.B],
    },
  ],
  [HealthScore.C]: [
    {
      key: "has_profile_description",
      translationKey: "has_profile_description",
      checkFunction: checkHasProfileDescription,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
    {
      key: "has_regular_hours_periods",
      translationKey: "has_regular_hours_periods",
      checkFunction: checkHasRegularHoursPeriods,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
    {
      key: "has_min_photo_count",
      translationKey: "has_min_photo_count",
      checkFunction: checkHasMinPhotoCount,
      requiredForScores: [
        HealthScore.A_PLUS,
        HealthScore.A,
        HealthScore.B,
        HealthScore.C,
      ],
    },
  ],
  [HealthScore.D]: [],
};
